<template>
  <div class="md:mx-14 mx-5">
    <div class="bg-root-1 -mt-6 z-50 absolute text-sm text-gray-40">
      Batch Details
    </div>
    <div
      @click="goBack"
      class="top-28 z-50 cursor-pointer flex items-center justify-start w-20"
    >
      <back-arrow />
      <p class="text-13px text-root-4 ml-2 font-normal">Back</p>
    </div>
    <!-- Batch Information grid-->
    <div class="mt-6" v-if="isLoading">
      <spinner
        size="medium"
        :spacing="40"
        message=""
        :line-fg-color="$primaryColor"
      />
    </div>
    <div v-else>
      <div class="grid md:grid-cols-12 grid-cols-1 gap-5 mt-8 w-full">
        <div class="md:col-span-6 w-full card bg-root">
          <div class="p-6">
            <div class="flex items-center">
              <batch-icon />
              <div>
                <p class="text-gray-40 text-xs font-normal">Batch ID</p>
                <p class="text-lg font-bold text-root-4">
                  {{ routeDetails.id }}
                </p>
              </div>
            </div>
            <div class="w-full border-b"></div>
            <div class="mt-3">
              <p class="text-root-4 font-light text-13px mb-3">
                Type:
                <span class="text-gray-40 capitalize">
                  {{ batchDetails.type }}
                </span>
              </p>
              <div class="flex mb-3 items-center">
                <p class="text-root-4 font-normal text-13px">
                  Destination Currency:
                </p>
                <div class="flex items-center">
                  <div class="ml-1">
                    <currency-icons
                      :currency="getCurrencyCountry(batchDetails.currency)"
                    />
                  </div>
                  <span class="text-gray-40 ml-0.5 font-normal text-13px">{{
                    batchDetails.currency
                  }}</span>
                </div>
              </div>
              <div
                class="text-root-4 flex items-center font-normal text-13px mb-3"
              >
                <div class="mr-1">Batched on:</div>
                <div class="text-gray-40 font-normal text-13px">
                  {{ routeDetails.id }}
                </div>
              </div>
              <p class="text-root-4 font-normal text-13px">
                {{
                  batchDetails.transactionCount > 1
                    ? 'Transactions:'
                    : 'Transaction:'
                }}
                <span class="text-gray-40">{{
                  batchDetails.transactionCount
                }}</span>
              </p>
            </div>
          </div>
        </div>
        <div class="md:col-span-6 card bg-root">
          <div class="p-6">
            <div class="flex gap-x-3 items-center">
              <p class="text-gray-3 text-2xl">$</p>
              <p class="text-base font-normal text-root-4">Total Amount</p>
            </div>
            <div class="text-4xl mb-2 text-root-4 mt-2 font-bold">
              {{ parseMoneyAll(batchDetails.amount, batchDetails.currency) }}
            </div>
            <div class="flex items-start justify-start">
              <div
                class="text-center text-xs capitalize px-1.5 py-0.5 font-normal border-none rounded-lg"
                :class="statusClass(batchDetails.status)"
              >
                {{ batchDetails.status }}
              </div>
            </div>
            <div
              v-if="batchDetails.status === 'unsettled'"
              class="flex w-full pt-11 items-center"
            >
              <div
                v-if="batchDetails.type !== 'withdrawal'"
                class="w-1/2 cursor-pointer bg-primary text-white px-2 py-3.5 rounded-3xl flex items-center mr-5 justify-center font-normal"
                @click="openDialog"
              >
                Settle
              </div>
              <div
                @click="cancelBatch"
                class="cursor-pointer border text-root-4 px-2 py-3.5 rounded-3xl flex items-center justify-center font-normal"
                :class="batchDetails.type !== 'withdrawal' ? 'w-1/2' : 'w-full'"
              >
                Cancel
              </div>
            </div>
            <div
              v-if="
                batchDetails.status === $STATUS.PENDING ||
                batchDetails.status === $STATUS.COMPLETED ||
                batchDetails.status === $STATUS.PROCESSING
              "
            >
              <div
                @click="showBatchTransactionDialog"
                class="w-full cursor-pointer border border-primary text-primary p-2 rounded-3xl flex items-center justify-center font-normal md:mt-10"
              >
                View Payment Receipt
              </div>
            </div>
            <div v-if="batchDetails.status === 'cancelled'">
              <div
                @click="showBatchTransactionDialog"
                class="w-full cursor-pointer border text-root-4 p-2 rounded-3xl flex items-center justify-center font-normal md:mt-10"
              >
                View Reason
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Transactions-->
      <div>
        <div class="mt-6">
          <p class="text-lg font-bold text-root-4">Transactions</p>
        </div>
        <div class="card2 bg-root p-6 mt-4">
          <div class="mt-8">
            <transactions-table
              :tableData="batchDetailsTableData"
              :currencySymbol="currencySymbol"
              :page="page"
              :transferType="transferType"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- dialogs -->
    <settle-deposit-dialog
      :showDialog="showSettleDepositDialog"
      @closeDialog="closeSettleDepositDialog"
      v-if="showSettleDepositDialog"
      :batchDetails="batchDetails"
      @showWallet="showWallet"
    />
    <settle-customer-transfer-dialog
      :showDialog="showSettleCustomerTransferDialog"
      :batchDetails="batchDetails"
      @closeDialog="closeDialog"
      v-if="showSettleCustomerTransferDialog"
    />
    <cancel-batch-dialog
      :showDialog="showCancelBatchDialog"
      @closeDialog="closeCancelBatchDialog"
      v-if="showCancelBatchDialog"
      :batchDetails="batchDetails"
      @showDisapproveDialog="showDisapproveDialog"
    />
    <disapprove-batch-dialog
      :showDialog="showDisapproveBatchDialog"
      v-if="showDisapproveBatchDialog"
      @closeDialog="closeDisapproveBatchDialog"
      :batchDetails="batchDetails"
      @showDisapproveDoneDialog="showDisapproveDoneDialog"
    />
    <done-dialog
      :showDialog="showDoneDialog"
      v-if="showDoneDialog"
      @closeDialog="closeDoneDialog"
    />
    <external-api-transaction-dialog
      :showDialog="showTransactionDialog"
      v-if="showTransactionDialog"
      @closeDialog="closeTransactionDialog"
      :transaction="batchTransactionDetails"
      :batchTransactionDetails="batchTransactionDetails"
      :transferType="transferType"
    />
    <wallet-dialog
      v-if="showWalletDialog"
      @closeDialog="closeWalletDialog"
      @goBack="settleBatch"
      :showDialog="showWalletDialog"
      :walletAddress="walletAddress"
    />
  </div>
</template>

<script>
import { COUNTRIES } from '@/utils/constants.js'
import getBatchDetails from '@/mixins/externalApi.js'
import { ParseMoneyAll, GetCurrencyCountry } from '@/utils/helpers.js'
import localDeposit from '@/mixins/localDeposit.js'
import statusClass from '@/mixins/global.js'
export default {
  name: 'BatchDetails',
  mixins: [statusClass, getBatchDetails, localDeposit],
  data () {
    return {
      countries: COUNTRIES,
      showSettleCustomerTransferDialog: false,
      showDisapproveBatchDialog: false,
      showCancelBatchDialog: false,
      showDoneDialog: false,
      currencySymbol: '',
      search: '',
      showSettleDepositDialog: false,
      isLoading: false,
      showTransactionDialog: false,
      showWalletDialog: false,
      walletAddress: '',
      payload: {
        page: 1,
        size: 10,
        status: '',
        type: '',
        from: '',
        to: ''
      }
    }
  },
  components: {
    BackArrow: () => import('@/assets/icons/BackArrow.vue'),
    BatchIcon: () => import('@/assets/icons/BatchIcon.vue'),
    TransactionsTable: () =>
      import('@/components/externalApi/tables/TransactionsTable.vue'),
    SettleDepositDialog: () =>
      import('@/components/externalApi/dialogs/SettleDepositDialog'),
    SettleCustomerTransferDialog: () =>
      import(
        '@/components/externalApi/dialogs/SettleCustomerTransferDialog.vue'
      ),
    CancelBatchDialog: () =>
      import('@/components/externalApi/dialogs/CancelBatchDialog.vue'),
    DisapproveBatchDialog: () =>
      import('@/components/externalApi/dialogs/DisapproveBatchDialog.vue'),
    DoneDialog: () => import('@/components/externalApi/dialogs/DoneDialog.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    WalletDialog: () => import('@/components/deposit/WalletDialog.vue'),
    ExternalApiTransactionDialog: () =>
      import(
        '@/components/externalApi/dialogs/ExternalApiTransactionDialog.vue'
      )
  },
  computed: {
    routeDetails () {
      return {
        id: this.$route.params.id,
        status: this.$route.query.status,
        currency: this.$route.query.currency,
        type: this.$route.query.type
      }
    },
    batchDetails () {
      return this.$store.state.externalApi.batchDetails.items
    },
    batchDetailsTableData () {
      return this.$store.state.externalApi.batchDetails.items[this.transferType]
    },
    page () {
      return this.$store.state.externalApi.batchDetails.page
    },
    transferType () {
      return this.$store.state.externalApi.batchDetails.items.type
    },
    batchTransactionDetails () {
      return this.$store.state.externalApi.batchDetails.items
    }
  },
  methods: {
    showBatchTransactionDialog () {
      this.showTransactionDialog = true
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    showWallet (walletAddress) {
      this.walletAddress = walletAddress
      this.showWalletDialog = true
    },
    closeTransactionDialog () {
      this.showTransactionDialog = false
    },
    goBack () {
      this.$router.push('/api-management')
      this.$store.dispatch('externalApi/setActiveComponent', 'Settlements')
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    showDisapproveDialog () {
      this.showDisapproveBatchDialog = true
    },
    closeDisapproveBatchDialog () {
      this.showDisapproveBatchDialog = false
    },
    closeSettleDepositDialog () {
      this.showSettleDepositDialog = false
    },
    closeDoneDialog () {
      this.showDoneDialog = false
    },
    showDisapproveDoneDialog () {
      this.showDoneDialog = true
      this.closeDisapproveBatchDialog()
    },
    cancelBatch () {
      this.showCancelBatchDialog = true
    },
    closeCancelBatchDialog () {
      this.showCancelBatchDialog = false
    },
    settleBatch () {
      if (this.batchDetails.type === 'transfer') {
        const country = COUNTRIES.find(
          (val) => val.currency === this.routeDetails.currency
        ).currency
        this.$store.dispatch('common/setSecondaryCurrency', country)
        const activeCurrency = this.$store.state.auth.transferDetails.find(
          (detail) => detail.from === country
        )
        this.$store.dispatch('common/setActiveCurrency', activeCurrency.to)
        this.showSettleCustomerTransferDialog = true
      } else {
        this.showSettleDepositDialog = true
      }
    },
    closeDialog () {
      this.showSettleCustomerTransferDialog = false
    },
    openDialog () {
      this.settleBatch()
    },
    closeWalletDialog () {
      this.showWalletDialog = false
    },
    getAllBatchDetails () {
      if (
        this.routeDetails.type === 'withdrawal' ||
        this.routeDetails.type === 'deposit'
      ) {
        this.getTreasuryBatchDetails()
      } else {
        this.getBatchDetails()
      }
    }
  },
  async created () {
    if (this.exchangeRate === 0) {
      const message = 'Cannot get current exchange rate at the moment'
      this.$message({
        showClose: true,
        message: `${message}, please try again Later`,
        type: 'error',
        duration: 5000
      })
      this.$store.dispatch('common/setError', message)
      setTimeout(() => {
        this.$router.push('/system-error')
      }, 3000)
    }
    await this.getWalletAddresses()
    await this.getAllBatchDetails()
    this.currencySymbol = COUNTRIES.find(
      (val) => val.currency === this.batchDetails.currency
    ).symbol
  },
  destroyed () {
    this.$store.dispatch('transactions/setCurrentTransaction', {})
    this.$store.dispatch('transactions/setTransactionDetails', {})
  }
}
</script>
<style scoped>
.card {
  /* height: 246px; */
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.12),
    0px 2px 4px rgba(3, 2, 7, 0.12);
  border-radius: 15px;
}
.grey-bg {
  background: #e0e0e0;
}
.btn {
  background: rgba(1, 5, 44, 0.05);
  border: 0.719333px solid #c1c4ca;
  border-radius: 6px;
}
.card2 {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.12),
    0px 2px 4px rgba(3, 2, 7, 0.12);
  border-radius: 15px;
}
>>> .el-input__inner {
  border-radius: 28px;
  height: 30px;
}
</style>
